import {
  CAMERA,
  CHARGE_FILL_DURATION_MS,
  CHARGE_MAX_POWER,
  CHARGE_MIN_POWER,
  GOAL_MOUTH_END_RATIO,
  GOAL_MOUTH_START_RATIO,
  laneTargets,
  shotBandThresholds,
} from './constants';
import { Phase, ProjectedPoint, ShotBand, ShotLane, ShotResolution, Vec3 } from './types';

export const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const lerp = (start: number, end: number, amount: number) => start + (end - start) * amount;

export const lerpVec3 = (start: Vec3, end: Vec3, amount: number): Vec3 => ({
  x: lerp(start.x, end.x, amount),
  y: lerp(start.y, end.y, amount),
  z: lerp(start.z, end.z, amount),
});

export const easeInOutSine = (value: number) => -(Math.cos(Math.PI * value) - 1) / 2;

export const easeOutCubic = (value: number) => 1 - Math.pow(1 - value, 3);

export const easeOutBack = (value: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(value - 1, 3) + c1 * Math.pow(value - 1, 2);
};

const hash32 = (value: number) => {
  let hash = value | 0;
  hash = Math.imul(hash ^ 0x3d20adea, 0x45d9f3b);
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x45d9f3b);
  hash ^= hash >>> 15;
  return hash >>> 0;
};

export const createShotSeed = (shotId: number, power: number) => hash32(Math.imul(shotId, 4099) ^ Math.round(power * 1000));

export const deterministicRoll = (seed: number, salt: number) => hash32(seed ^ Math.imul(salt + 1, 0x9e3779b1)) / 0xffffffff;

export const clampAim = (value: number) => clamp(value, 0, 1);

export const getAimFromStageRatio = (ratio: number) =>
  clampAim((ratio - GOAL_MOUTH_START_RATIO) / (GOAL_MOUTH_END_RATIO - GOAL_MOUTH_START_RATIO));

export const getChargePower = (chargeElapsedMs: number) =>
  lerp(CHARGE_MIN_POWER, CHARGE_MAX_POWER, clamp(chargeElapsedMs / CHARGE_FILL_DURATION_MS, 0, 1));

export const getShotBand = (power: number): ShotBand => {
  if (power <= shotBandThresholds.redLowMax || power >= shotBandThresholds.redHighMin) {
    return 'red';
  }

  if (power >= shotBandThresholds.greenStart && power <= shotBandThresholds.greenEnd) {
    return 'green';
  }

  return 'yellow';
};

export const getPowerValue = (
  phase: Phase,
  nowMs: number,
  shot: ShotResolution | null,
  chargeStartedAtMs: number | null,
): number => {
  if (phase === 'aim') {
    return chargeStartedAtMs === null ? CHARGE_MIN_POWER : getChargePower(Math.max(0, nowMs - chargeStartedAtMs));
  }

  return shot?.power ?? CHARGE_MIN_POWER;
};

export const getPreviewLane = (aim: number): ShotLane => {
  if (aim < 0.34) {
    return 'left';
  }

  if (aim > 0.66) {
    return 'right';
  }

  return 'center';
};

export const getPreviewTarget = (aim: number, power: number): Vec3 => {
  const clampedAim = clampAim(aim);
  const laneBlend =
    clampedAim <= 0.5
      ? lerpVec3(laneTargets.left, laneTargets.center, clampedAim / 0.5)
      : lerpVec3(laneTargets.center, laneTargets.right, (clampedAim - 0.5) / 0.5);

  return {
    x: laneBlend.x,
    y: lerp(0.82, 3.18, power),
    z: laneBlend.z,
  };
};

export const projectPoint = (point: Vec3, width: number, height: number): ProjectedPoint => {
  const dx = point.x - CAMERA.x;
  const dy = point.y - CAMERA.y;
  const dz = Math.max(3.4, point.z - CAMERA.z);
  const aspectRatio = height / Math.max(width, 1);
  const tallMobileFrame = width <= 430 && aspectRatio > 1.35;
  const tallMobileZoom = tallMobileFrame ? Math.max(0.92, 1 - (aspectRatio - 1.18) * 0.08) : 1;
  const focalLength = Math.min(width, height) * 1.84 * tallMobileZoom;
  const verticalAnchor = tallMobileFrame ? height * 0.59 : height * 0.62;
  const scale = focalLength / dz;

  return {
    x: width / 2 + dx * scale,
    y: verticalAnchor - dy * scale,
    scale,
  };
};

export const getKeeperOrigin = (lane: ShotLane, targetY: number) => ({
  x: laneTargets[lane].x * 0.6,
  y: Math.max(0.25, targetY * 0.38),
  z: 53.8,
});
